import { ref } from 'vue'
import { apiPost } from '@/composables/useApi'
import { summarizeSpeech } from '@/utils/levelTest'
import { useVadSpeechRecorder } from '@/composables/player/useVadSpeechRecorder'
import { createSpeechOnsetBuffer } from '@/utils/audio/speechOnsetBuffer'

const SAMPLE_RATE = 16000
// Azure 令牌 10 分钟有效；一次口语测试 3 分钟左右，共用一个令牌，快过期时换新
const TOKEN_USABLE_MS = 8 * 60 * 1000
// 停止录音后等识别把最后一段和评分送回来的最长时间
const RESULT_WAIT_MS = 10000
const RECOGNITION_LANGUAGE = 'en-US'

// 口语定级测试的麦克风和逐题识别：PCM 推给 Azure 做发音评估。
// 朗读题带原文（有参考文本评估），问答题不带（无参考文本评估，同时拿到转写稿）。
export function useLevelTestRecorder() {
  const level = ref(0)
  const micReady = ref(false)
  // { quota: 是否因为语音资源额度用完, message }
  const recognitionError = ref(null)
  const speechDetected = ref(false)

  let sdkPromise = null
  let token = null
  let stream = null
  let audioContext = null
  let analyser = null
  let levelFrame = 0
  let frameSink = null
  let onSpeech = null
  let cleanupPromise = null
  let useFallback = false
  const vad = useVadSpeechRecorder({
    getStream: async () => stream,
    onSpeechStart: () => onSpeech?.(),
    onFrameProcessed: (_probabilities, frame) => frameSink?.(frame),
  })

  function loadSdk() {
    if (!sdkPromise) sdkPromise = import('microsoft-cognitiveservices-speech-sdk')
    return sdkPromise
  }

  async function ensureToken() {
    if (token && Date.now() - token.fetchedAt < TOKEN_USABLE_MS) return token
    const result = await apiPost('/api/speech-token', useFallback ? { fallback: true } : {})
    token = { token: result.token, region: result.region, fetchedAt: Date.now() }
    return token
  }

  async function openMic() {
    await cleanupPromise
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 }
    })
    audioContext = new AudioContext()
    const source = audioContext.createMediaStreamSource(stream)
    analyser = audioContext.createAnalyser()
    analyser.fftSize = 512
    source.connect(analyser)
    trackLevel()
    micReady.value = true
    // 提前加载 SDK 和令牌，第一题不用等
    try { await Promise.all([loadSdk(), ensureToken(), vad.prepare()]) }
    catch (error) { await closeMic(); throw error }
  }

  function trackLevel() {
    const samples = new Uint8Array(analyser.fftSize)
    const tick = () => {
      analyser.getByteTimeDomainData(samples)
      let peak = 0
      for (const sample of samples) peak = Math.max(peak, Math.abs(sample - 128))
      level.value = Math.min(1, peak / 64)
      levelFrame = requestAnimationFrame(tick)
    }
    tick()
  }

  // 开始录一题；referenceText 为空时做无参考文本评估。返回的 finish() 给出汇总指标
  async function startRecording(referenceText = '') {
    const [sdk, current] = await Promise.all([loadSdk(), ensureToken()])
    const config = sdk.SpeechConfig.fromAuthorizationToken(current.token, current.region)
    config.speechRecognitionLanguage = RECOGNITION_LANGUAGE
    const pushStream = sdk.AudioInputStream.createPushStream(sdk.AudioStreamFormat.getWaveFormatPCM(SAMPLE_RATE, 16, 1))
    const recognizer = new sdk.SpeechRecognizer(config, sdk.AudioConfig.fromStreamInput(pushStream))
    const assessment = new sdk.PronunciationAssessmentConfig(
      referenceText,
      sdk.PronunciationAssessmentGradingSystem.HundredMark,
      sdk.PronunciationAssessmentGranularity.Word,
      Boolean(referenceText)
    )
    assessment.applyTo(recognizer)

    const segments = []
    let resolveStopped
    const stopped = new Promise(resolve => {
      resolveStopped = resolve
    })
    recognizer.recognized = (_sender, event) => {
      if (event.result.reason !== sdk.ResultReason.RecognizedSpeech || !event.result.text) return
      segments.push(JSON.parse(event.result.properties.getProperty(sdk.PropertyId.SpeechServiceResponse_JsonResult)))
    }
    recognizer.canceled = (_sender, event) => {
      if (event.reason === sdk.CancellationReason.Error) {
        const message = event.errorDetails || 'Speech recognition failed'
        recognitionError.value = { quota: /quota/i.test(message), message }
      }
      resolveStopped()
    }
    recognizer.sessionStopped = () => resolveStopped()

    speechDetected.value = false
    let startPromise = null
    let closed = false
    let pendingFrames = []
    let ready = false
    const buffer = createSpeechOnsetBuffer(frame => {
      if (ready) pushStream.write(floatToPcm16(frame))
      else pendingFrames.push(frame)
    })
    const sink = frame => buffer.push(frame)
    frameSink = sink
    onSpeech = () => {
      if (startPromise || closed) return
      speechDetected.value = true
      buffer.open()
      startPromise = new Promise((resolve, reject) => recognizer.startContinuousRecognitionAsync(resolve, reject))
        .then(() => {
          ready = true
          pendingFrames.forEach(frame => pushStream.write(floatToPcm16(frame)))
          pendingFrames = []
        }).catch(error => {
          recognitionError.value = { quota: /quota/i.test(String(error)), message: String(error) }
        })
    }
    try { await vad.start() }
    catch (error) { frameSink = null; onSpeech = null; recognizer.close(); pushStream.close(); throw error }
    const startedAt = performance.now()

    async function stop() {
      if (frameSink === sink) { frameSink = null; onSpeech = null }
      closed = true
      await vad.pause()
      await startPromise
      pushStream.close()
    }

    async function close() {
      if (startPromise) await new Promise(resolve => recognizer.stopContinuousRecognitionAsync(resolve, resolve))
      recognizer.close()
    }

    return {
      async finish() {
        const durationSeconds = (performance.now() - startedAt) / 1000
        try {
          await stop()
          if (startPromise) await Promise.race([stopped, wait(RESULT_WAIT_MS)])
        } finally { await close() }
        return summarizeSpeech(segments, durationSeconds)
      },
      async cancel() {
        try { await stop() } finally { await close() }
      }
    }
  }

  // 主语音资源额度用完时换备用资源；已经换过就返回 false
  function switchToFallback() {
    if (useFallback) return false
    useFallback = true
    token = null
    recognitionError.value = null
    return true
  }

  function closeMic() {
    if (cleanupPromise) return cleanupPromise
    frameSink = null
    onSpeech = null
    cancelAnimationFrame(levelFrame)
    const oldStream = stream
    const oldContext = audioContext
    stream = null
    audioContext = null
    micReady.value = false
    speechDetected.value = false
    level.value = 0
    oldStream?.getTracks().forEach(track => track.stop())
    cleanupPromise = (async () => {
      try { await vad.destroy() }
      finally { await oldContext?.close() }
    })().finally(() => { cleanupPromise = null })
    return cleanupPromise
  }

  return { level, micReady, speechDetected, recognitionError, openMic, startRecording, switchToFallback, closeMic }
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function floatToPcm16(input) {
  const output = new Int16Array(input.length)
  for (let index = 0; index < output.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, input[index]))
    output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
  }
  return output.buffer
}
